import React, { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';

/**
 * Fixed-position tooltip that renders via portal to avoid overflow clipping.
 * Positioned to the right of the triggering element using getBoundingClientRect.
 */
function SidebarTooltip({ text, anchorRef, visible }) {
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (visible && anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      setPos({
        top: rect.top + rect.height / 2,
        left: rect.right + 8,
      });
    }
  }, [visible, anchorRef]);

  if (!text || !visible) return null;

  return createPortal(
    <div
      className="fixed pointer-events-none z-[9999]"
      style={{
        top: pos.top,
        left: pos.left,
        transform: 'translateY(-50%)',
        opacity: visible ? 1 : 0,
        transition: 'opacity 120ms ease',
      }}
    >
      {/* Arrow */}
      <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-[4px] w-0 h-0 border-y-4 border-y-transparent border-r-4 border-r-gray-800" />
      {/* Pill */}
      <div className="bg-gray-800 text-white text-[11px] font-medium rounded px-2 py-1 whitespace-nowrap shadow-lg">
        {text}
      </div>
    </div>,
    document.body
  );
}

/**
 * Wrapper that adds hover tooltip to any sidebar button.
 * Uses a ref to calculate fixed position for the tooltip portal.
 */
function WithTooltip({ title, children }) {
  const [hovered, setHovered] = useState(false);
  const ref = useRef(null);
  return (
    <div
      ref={ref}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {children}
      <SidebarTooltip text={title} anchorRef={ref} visible={hovered} />
    </div>
  );
}

/**
 * DraggableMiniSidebar — a 44px-wide sidebar with a pinned top button,
 * a draggable list of filter items, floating tooltips, and an optional
 * export button at bottom.
 *
 * Props:
 *  - topButton: { label, title, icon?, active, onClick }
 *  - items: [{ key, label, title, badge?, active, onClick, icon? }]
 *  - onReorder: (newItems) => void
 *  - onExport: () => void
 */
export default function DraggableMiniSidebar({ topButton, items, onReorder, onExport }) {
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const dragNodeRef = useRef(null);

  const handleDragStart = useCallback((e, idx) => {
    setDragIdx(idx);
    dragNodeRef.current = e.target;
    e.dataTransfer.effectAllowed = 'move';
    const ghost = document.createElement('div');
    ghost.style.opacity = '0';
    document.body.appendChild(ghost);
    e.dataTransfer.setDragImage(ghost, 0, 0);
    setTimeout(() => document.body.removeChild(ghost), 0);
  }, []);

  const handleDragOver = useCallback((e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (idx !== overIdx) setOverIdx(idx);
  }, [overIdx]);

  const handleDrop = useCallback((e, dropIdx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === dropIdx) {
      setDragIdx(null);
      setOverIdx(null);
      return;
    }
    const reordered = [...items];
    const [moved] = reordered.splice(dragIdx, 1);
    reordered.splice(dropIdx, 0, moved);
    onReorder(reordered);
    setDragIdx(null);
    setOverIdx(null);
  }, [dragIdx, items, onReorder]);

  const handleDragEnd = useCallback(() => {
    setDragIdx(null);
    setOverIdx(null);
  }, []);

  return (
    <div className="w-11 bg-emerald-50 border-r border-emerald-100 flex-shrink-0 flex flex-col pt-2 gap-1 items-center overflow-y-auto">
      {/* Pinned top button */}
      {topButton && (
        <WithTooltip title={topButton.title}>
          <button
            onClick={topButton.onClick}
            className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors flex-shrink-0 text-[9px] font-bold ${
              topButton.active
                ? 'bg-emerald-100 text-emerald-700'
                : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
            }`}
          >
            {topButton.icon || topButton.label}
          </button>
        </WithTooltip>
      )}

      {topButton && items.length > 0 && <div className="w-5 border-t border-gray-200 my-0.5 flex-shrink-0" />}

      {/* Draggable items */}
      {items.map((item, idx) => {
        const isDragging = dragIdx === idx;
        const isOver = overIdx === idx && dragIdx !== idx;

        return (
          <div
            key={item.key}
            draggable
            onDragStart={(e) => handleDragStart(e, idx)}
            onDragOver={(e) => handleDragOver(e, idx)}
            onDrop={(e) => handleDrop(e, idx)}
            onDragEnd={handleDragEnd}
            className="flex-shrink-0"
            style={{
              opacity: isDragging ? 0.4 : 1,
              transform: isOver ? 'scale(1.1)' : 'scale(1)',
              transition: 'transform 150ms ease, opacity 150ms ease',
            }}
          >
            {/* Drop indicator above */}
            {isOver && dragIdx !== null && dragIdx > idx && (
              <div className="w-6 h-0.5 bg-emerald-400 rounded-full mx-auto mb-0.5" />
            )}

            <WithTooltip title={item.title}>
              <button
                onClick={item.onClick}
                className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors text-[9px] font-bold relative cursor-grab active:cursor-grabbing ${
                  item.active
                    ? 'bg-emerald-100 text-emerald-700 ring-1 ring-emerald-300'
                    : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
                }`}
              >
                {item.icon || item.label}
                {item.badge != null && item.badge > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-emerald-500 text-white text-[7px] rounded-full flex items-center justify-center font-bold">
                    {item.badge > 99 ? '99' : item.badge}
                  </span>
                )}
              </button>
            </WithTooltip>

            {/* Drop indicator below */}
            {isOver && dragIdx !== null && dragIdx < idx && (
              <div className="w-6 h-0.5 bg-emerald-400 rounded-full mx-auto mt-0.5" />
            )}
          </div>
        );
      })}

      {/* Spacer pushes export to bottom */}
      <div className="flex-1" />

      {/* Export button — pinned at bottom */}
      {onExport && (
        <>
          <div className="w-5 border-t border-gray-200 my-0.5 flex-shrink-0" />
          <WithTooltip title="Export current list">
            <button
              onClick={onExport}
              className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors flex-shrink-0 text-gray-400 hover:bg-gray-100 hover:text-gray-600 mb-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
            </button>
          </WithTooltip>
        </>
      )}
    </div>
  );
}
